'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../api/client';

type BillingSummary = {
  memberCount: number;
  totalCents: number;
  trialEndedAt: string | null;
};

export function useBillingSummary() {
  const {
    getToken,
    isLoaded,
    isSignedIn,
    orgId: clerkOrganizationId,
  } = useAuth();

  return useQuery<BillingSummary | null>({
    queryKey: ['billing-summary', clerkOrganizationId],
    enabled: isLoaded && isSignedIn === true && !!clerkOrganizationId,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.GET(
        '/api/v1/billing/pricing',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (error || !response.ok || !data) {
        throw new Error('Failed to fetch billing summary');
      }

      // Data comes directly from pricing endpoint (flat structure)
      if (
        !data.memberCount ||
        data.totalCents === null ||
        data.totalCents === undefined
      ) {
        return null;
      }

      return {
        memberCount: data.memberCount,
        totalCents: data.totalCents,
        trialEndedAt: data.trialEndedAt ?? null,
      };
    },
    staleTime: 30000,
    refetchInterval: 60000,
    retry: false,
  });
}
